import React, { useContext, useState, useEffect } from 'react'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import ValuePoint from 'admin/components/ValuePoint'
import {
  Card,
  Grid,
  Typography,
  Box,
  Button,
  Collapse,
  Tooltip,
  Fade,
  IconButton,
} from '@mui/material'
import { ExpandMore, Help } from '@mui/icons-material'

const sickLeaveValuesList = (num, count = 5, step = 174) => {
  const half = Math.floor(count / 2)
  let values = Array.from({ length: count }, (_, i) => num + (i - half) * step)
  if (values.some((v) => v < 0)) {
    values = values.filter((v) => v >= 0)
    while (values.length < count) {
      values.push(values[values.length - 1] + step)
    }
  }
  if (!values.includes(0) && Math.min(...values) < step) {
    values = [0, ...values.filter((v) => v > 0).slice(0, count - 1)]
  }
  return values.join(', ')
}

const dollarFormat = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

const dollarCentsFormat = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

function LeaveAndEarnings({
  collapseSections,
  toggleCollapse,
  monthOfExtraServiceSick,
  optimalSickLeave,
  intakeState,
  updateIntakeStateAtKey,
  updateIntakeStateAndSubmit,
}) {
  const [localForm, setLocalForm] = useState({
    baseSalary: intakeState.baseSalary || '',
    highThree: intakeState.highThree || '',
    plannedSickLeave: intakeState.plannedSickLeave || '',
    currentLeaveBalance: intakeState.currentLeaveBalance || '',
    currentSickLeaveBalance: intakeState.currentSickLeaveBalance || '',
  })
  const handleChange = (field) => (value) => {
    setLocalForm((prev) => ({ ...prev, [field]: value }))
  }
  return (
    <>
      <Grid item xs={12} md={4} xl={3}>
        <Card
          sx={{
            overflow: 'visible',
            textAlign: 'left',
            boxShadow: '0px 4px 25px -8px rgba(0,0,0,0.2)',
            backgroundColor: '#f8f9fa',
            borderRadius: 2,
            p: 2,
          }}
        >
          <Grid container columnSpacing={3} rowSpacing={2}>
            <Grid item xs={12}>
              <Typography variant="valueTitle">VALUE POINTS</Typography>
              <Collapse
                in={collapseSections.leaveAndEarnings}
                style={{ width: '100%' }}
              >
                {!intakeState?.highThree && (
                  <ValuePoint text="Missing High Three" type="warning" />
                )}
                <ValuePoint text="Strategy - Discuss High Three Calc" />
                <ValuePoint text="Strategy - Discuss Interim Period" />
                <ValuePoint
                  text={`Value of Extra Sick Leave Month: ${dollarCentsFormat.format(
                    monthOfExtraServiceSick
                  )}`}
                  type="data"
                />
                <Tooltip
                  placement="top"
                  color="info"
                  title={`List of Optimal Sick Leave Values: ${sickLeaveValuesList(
                    optimalSickLeave
                  )}`}
                >
                  <ValuePoint
                    text={`Target Sick Leave Balance: ${optimalSickLeave}`}
                    type="data"
                    isTooltip={true}
                  />
                </Tooltip>
                <ValuePoint
                  text={`Target Sick Leave Months: ${Math.floor(
                    optimalSickLeave / 174
                  )}`}
                  type="data"
                />
                <ValuePoint
                  text={`Estimated Value of Annual Leave GROSS: ${dollarFormat.format(
                    (intakeState?.baseSalary / 2087) *
                      (intakeState?.currentLeaveBalance || 0)
                  )}`}
                  type="data"
                />
                <ValuePoint
                  text={`Estimated Value of Annual Leave NET: ${dollarFormat.format(
                    (intakeState?.baseSalary / 2087) *
                      (intakeState?.currentLeaveBalance || 0) *
                      0.6855
                  )}`}
                  type="data"
                />
              </Collapse>
            </Grid>
          </Grid>
        </Card>
      </Grid>
      <Grid item xs={12} md={8} xl={9}>
        <Card
          id="paystub"
          sx={{
            overflow: 'visible',
            textAlign: 'left',
            boxShadow: '0px 4px 25px -8px rgba(0,0,0,0.2)',
            borderRadius: 2,
            p: 2,
            pb: 0,
          }}
        >
          <Grid container columnSpacing={3} rowSpacing={2} pb={2}>
            <Grid
              item
              xs={12}
              onClick={() => toggleCollapse('leaveAndEarnings')}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography variant="h4" sx={{ flexGrow: 1 }}>
                Leave & Earnings
              </Typography>
              <ExpandMore
                sx={{
                  height: '1.5em',
                  width: '1.5em',
                  transform: collapseSections.leaveAndEarnings
                    ? 'rotate(180deg)'
                    : 'rotate(0deg)',
                  transition: 'transform 0.3s ease-in-out',
                }}
              />
            </Grid>
          </Grid>
          <Collapse
            in={collapseSections.leaveAndEarnings}
            style={{ width: '100%' }}
          >
            <Grid container columnSpacing={3} rowSpacing={2} pb={2}>
              <Grid item xs={12} sm={6} md={4}>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'baseSalary',
                  label: (
                    <>
                      <span>
                        Adjusted Base Annual Salary
                        <span style={{ color: 'red' }}> *</span>
                        <Tooltip
                          TransitionComponent={Fade}
                          title="Your Adjusted base salary is your base salary including any locality pay you receive."
                          placement="top"
                          color="info"
                        >
                          <IconButton size="small" color="dark">
                            <Help sx={{ marginTop: '-10px' }} />
                          </IconButton>
                        </Tooltip>
                      </span>
                    </>
                  ),
                  placeholder: '$62,000.00',
                  inputType: 'currencyFloat',
                  value: localForm.baseSalary,
                  onChange: (e) => handleChange('baseSalary')(e),
                  onBlur: updateIntakeStateAndSubmit((baseSalary) =>
                    updateIntakeStateAtKey({ baseSalary })
                  ),
                })}
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'highThree',
                  label: (
                    <>
                      <span>
                        High Three
                        <span style={{ color: 'red' }}> *</span>
                        <Tooltip
                          TransitionComponent={Fade}
                          title="Average of the highest three consecutive years of service. Used to calculate your pension"
                          placement="top"
                          color="info"
                        >
                          <IconButton size="small" color="dark">
                            <Help sx={{ marginTop: '-10px' }} />
                          </IconButton>
                        </Tooltip>
                      </span>
                    </>
                  ),
                  placeholder: dollarCentsFormat.format(
                    intakeState?.baseSalary
                      ? intakeState?.baseSalary * 0.96
                      : 62000
                  ),
                  inputType: 'currencyFloat',
                  value: localForm.highThree,
                  onChange: (e) => handleChange('highThree')(e),
                  onBlur: updateIntakeStateAndSubmit((highThree) =>
                    updateIntakeStateAtKey({ highThree })
                  ),
                })}
                <Typography variant="inputNote" component="p">
                  Calculated High Three:{' '}
                  {intakeState?.baseSalary
                    ? dollarCentsFormat.format(intakeState?.baseSalary * 0.96)
                    : 'Need Base Salary'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'currentLeaveBalance',
                  label: 'Current Earned Annual Leave Balance',
                  placeholder: '100.0',
                  inputType: 'floatField',
                  value: localForm.currentLeaveBalance,
                  onChange: (e) => handleChange('currentLeaveBalance')(e),
                  onBlur: updateIntakeStateAndSubmit((currentLeaveBalance) =>
                    updateIntakeStateAtKey({ currentLeaveBalance })
                  ),
                })}
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'currentSickLeaveBalance',
                  label: 'Current Sick Leave Balance',
                  placeholder: '100.0',
                  inputType: 'floatField',
                  value: localForm.currentSickLeaveBalance,
                  onChange: (e) => handleChange('currentSickLeaveBalance')(e),
                  onBlur: updateIntakeStateAndSubmit(
                    (currentSickLeaveBalance) =>
                      updateIntakeStateAtKey({ currentSickLeaveBalance })
                  ),
                })}
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'plannedSickLeave',
                  label: 'Planned Sick Leave At Retirement',
                  placeholder: '100.0',
                  inputType: 'floatField',
                  value: localForm.plannedSickLeave,
                  onChange: (e) => handleChange('plannedSickLeave')(e),
                  onBlur: updateIntakeStateAndSubmit((plannedSickLeave) =>
                    updateIntakeStateAtKey({ plannedSickLeave })
                  ),
                })}
              </Grid>
            </Grid>
          </Collapse>
        </Card>
      </Grid>
    </>
  )
}

export default LeaveAndEarnings
